/**
 * Quick view.
 *
 * Upgrades every [data-quick-view] link into a modal. The links are real
 * hrefs to the product page, so if this script fails to load or throws, the
 * bag icon still navigates somewhere useful rather than doing nothing.
 *
 * Option selection is rebuilt from the product JSON on each open. Variant
 * resolution matches on the full option tuple rather than option1 alone,
 * because a product with Colour + Size has several variants sharing option1.
 */
(() => {
  const dialog = document.querySelector('[data-quick-view-dialog]');
  if (!dialog || typeof dialog.showModal !== 'function') return;

  const el = {
    track: dialog.querySelector('[data-qv-track]'),
    title: dialog.querySelector('[data-qv-title]'),
    price: dialog.querySelector('[data-qv-price]'),
    footPrice: dialog.querySelector('[data-qv-foot-price]'),
    options: dialog.querySelector('[data-qv-options]'),
    description: dialog.querySelector('[data-qv-description]'),
    details: dialog.querySelector('[data-qv-details]'),
    measurement: dialog.querySelector('[data-qv-measurement]'),
    measurementInput: dialog.querySelector('[data-qv-measurement-input]'),
    units: [...dialog.querySelectorAll('[data-qv-unit]')],
    submit: dialog.querySelector('[data-qv-submit]'),
    buyNow: dialog.querySelector('[data-qv-buy-now]'),
    status: dialog.querySelector('[data-qv-status]'),
  };

  const strings = {
    selectSize: el.submit.textContent.trim(),
    enterMeasurement: 'Enter measurement',
    addToCart: dialog.dataset.addToCart || 'Add to cart',
    updateToCart: dialog.dataset.updateToCart || 'Update to cart',
    soldOut: dialog.dataset.soldOut || 'Sold out',
    added: dialog.dataset.added || 'Added to cart',
  };

  let product = null;
  let chosen = {};
  let openRequestId = 0;
  // When set, the popup is editing an existing cart line rather than adding a
  // new one. Its properties are copied so measurement edits never mutate the
  // cart snapshot before Shopify confirms the update.
  let editContext = null;
  // Whether the shopper has opened the custom-size measurement panel, and the
  // unit they picked. Reset on every open.
  let measurementMode = false;
  let measurementUnit = 'cm';
  let editSubmitting = false;
  const cache = new Map();

  // A finite, positive number, or null when the field is empty/invalid.
  /**
   * Accepted head measurements per unit — the same bounds the product page
   * enforces. Wider than the published size ladder (52-62 cm) because custom
   * sizing exists for heads outside it, but bounded so 0.01 cm and 999 in
   * cannot reach an order.
   */
  const MEASUREMENT_RANGE = {
    cm: { min: 40, max: 70, step: 0.1 },
    in: { min: 15.75, max: 27.5, step: 0.05 },
  };

  const measurementRange = () => MEASUREMENT_RANGE[measurementUnit] || MEASUREMENT_RANGE.cm;

  const measurementRangeMessage = () => {
    const range = measurementRange();
    return `Enter a head measurement between ${range.min} and ${range.max} ${measurementUnit}.`;
  };

  /**
   * Mirrors the active unit onto the input so native validation and the mobile
   * keypad agree with the check below.
   */
  const applyMeasurementBounds = () => {
    if (!el.measurementInput) return;
    const range = measurementRange();
    el.measurementInput.min = String(range.min);
    el.measurementInput.max = String(range.max);
    el.measurementInput.step = String(range.step);
  };

  const measurementValue = () => {
    const n = parseFloat(el.measurementInput?.value);
    if (!Number.isFinite(n)) return null;
    const range = measurementRange();
    return n >= range.min && n <= range.max ? n : null;
  };

  const resetMeasurement = (properties = null, open = false) => {
    const value = String(properties?.['Head measurement'] || '').trim();
    const unit = String(properties?.['Measurement unit'] || '').trim().toLowerCase();
    measurementMode = open;
    measurementUnit = ['cm', 'in'].includes(unit) ? unit : 'cm';
    if (el.measurementInput) el.measurementInput.value = value;
    applyMeasurementBounds();
    el.units.forEach((b) => {
      const on = b.dataset.qvUnit === measurementUnit;
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-pressed', String(on));
    });
    if (el.measurement) el.measurement.hidden = !open;
  };

  const money = (cents) => window.MSPMoney.format(cents);

  const renderPrice = (target, price, compareAt) => {
    if (!target) return;
    target.textContent = '';

    const now = document.createElement('span');
    now.textContent = money(price);
    if (compareAt > price) now.className = 'qv__price-sale';
    target.appendChild(now);

    if (compareAt > price) {
      const was = document.createElement('s');
      was.className = 'qv__price-was';
      was.textContent = money(compareAt);
      target.appendChild(was);
    }
  };

  /** Find the variant whose options match every current selection. */
  const currentVariant = () => {
    if (!product) return null;
    const names = product.options.map((o) => (typeof o === 'string' ? o : o.name));
    if (names.some((n) => !chosen[n])) return null;
    return product.variants.find((v) =>
      names.every((n, i) => v.options[i] === chosen[n])
    ) || null;
  };

  const normalizeOptionToken = (value) => String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[-‑–]/g, ' ')
    .replace(/\s+/g, ' ');
  const isCustomVariant = (variant) => variant?.options?.some((value) => (
    String(value || '').split('/').some((part) => (
      ['custom', 'bespoke', 'made to measure'].includes(normalizeOptionToken(part))
    ))
  ));

  /**
   * An option value is buyable only when an in-stock variant matches both
   * that value and every other option the shopper has already selected.
   * This prevents, for example, Size L from looking available for Yellow
   * merely because a different colour has an in-stock L variant.
   */
  const optionIsAvailable = (candidateIndex, candidateValue) => {
    if (!product) return false;
    const names = product.options.map((o) => (typeof o === 'string' ? o : o.name));
    return product.variants.some((variant) => (
      variant.available &&
      variant.options[candidateIndex] === candidateValue &&
      names.every((name, index) => (
        index === candidateIndex || !chosen[name] || variant.options[index] === chosen[name]
      ))
    ));
  };

  const syncOptionAvailability = () => {
    el.options.querySelectorAll('.qv__swatch').forEach((button) => {
      const index = Number(button.dataset.optionIndex);
      const available = optionIsAvailable(index, button.dataset.value);
      button.disabled = !available;
      button.classList.toggle('is-unavailable', !available);
      button.setAttribute('aria-disabled', String(!available));
    });
  };

  const renderState = () => {
    const v = currentVariant();
    // While editing a line the primary button commits an update, not an add.
    const cartLabel = editContext ? strings.updateToCart : strings.addToCart;

    if (v) {
      renderPrice(el.price, v.price, v.compare_at_price);
      renderPrice(el.footPrice, v.price, v.compare_at_price);
      if (v.featured_image && v.featured_image.src) {
        const match = [...el.track.children].find(
          (img) => img.dataset.src === v.featured_image.src
        );
        if (match) {
          el.track.scrollTo({ left: match.offsetLeft - el.track.offsetLeft, behavior: 'smooth' });
        }
      }
    }

    const names = product.options.map((o) => (typeof o === 'string' ? o : o.name));
    const incomplete = names.some((n) => !chosen[n]);
    const custom = v && isCustomVariant(v);
    syncOptionAvailability();

    // Leaving the custom option folds the measurement panel away again.
    if (!custom) measurementMode = false;
    if (el.measurement) {
      el.measurement.hidden = !(custom && v.available && measurementMode);
    }

    if (incomplete) {
      el.submit.textContent = strings.selectSize;
      el.submit.disabled = true;
    } else if (!v || !v.available) {
      el.submit.textContent = strings.soldOut;
      el.submit.disabled = true;
    } else if (custom) {
      // First click reveals the measurement panel; only once a valid
      // measurement is entered does the button commit the add.
      el.submit.textContent = measurementMode ? cartLabel : strings.enterMeasurement;
      el.submit.disabled = measurementMode && measurementValue() === null;
    } else {
      el.submit.textContent = cartLabel;
      el.submit.disabled = false;
    }
    if (editSubmitting) el.submit.disabled = true;

    // Buy now appears only once a real, buyable, non-custom variant is chosen —
    // and never while editing a line, where the only action is to update it.
    if (el.buyNow) {
      const canBuyNow = !editContext && !incomplete && !!v && v.available && !custom;
      el.buyNow.hidden = !canBuyNow;
      el.buyNow.disabled = !canBuyNow;
    }

    el.status.textContent = '';
  };

  /**
   * Renders every product image into the scroll-snap track. Falls back to
   * the featured image alone if the product has no images array, so the
   * panel never opens with an empty media column.
   */
  const renderGallery = (product) => {
    const sources = (product.images && product.images.length)
      ? product.images
      : [product.featured_image].filter(Boolean);

    el.track.innerHTML = '';
    sources.slice(0, 8).forEach((src, index) => {
      const img = document.createElement('img');
      img.className = 'qv__image';
      img.src = src;
      img.alt = index === 0 ? product.title : '';
      img.width = 800;
      img.height = 800;
      // The mobile filmstrip shows three thumbs at once, so the first three
      // are on screen the instant the sheet opens. Lazy-loading them leaves
      // two empty slots until the loader catches up.
      img.loading = index < 3 ? 'eager' : 'lazy';
      img.dataset.src = src;
      el.track.appendChild(img);
    });
    el.track.scrollTo({ left: 0 });
  };

  const renderOptions = () => {
    el.options.innerHTML = '';
    const names = product.options.map((o) => (typeof o === 'string' ? o : o.name));

    // A single-variant product has one synthetic option; nothing to choose.
    if (product.variants.length === 1 && names.length === 1 && names[0] === 'Title') {
      chosen[names[0]] = product.variants[0].options[0];
      return;
    }

    /**
     * Everything except size arrives pre-selected, from the first variant
     * that is actually in stock.
     *
     * Size is the one decision only the shopper can make, so it is the one
     * left open — choose it and the buy controls appear. Pre-selecting
     * colour too would show those controls before any real choice, and
     * leaving colour blank made the panel demand two picks for what reads
     * as a single decision.
     */
    const firstAvailable = product.variants.find((v) => v.available) || product.variants[0];
    if (firstAvailable) {
      names.forEach((name, index) => {
        if (/size/i.test(name)) return;
        chosen[name] = firstAvailable.options[index];
      });
    }

    names.forEach((name, index) => {
      const values = [...new Set(product.variants.map((v) => v.options[index]))];
      const isColour = /colou?r/i.test(name);

      const group = document.createElement('div');
      group.className = 'qv__option';

      /**
       * Colour reads "Color: Camel" — the swatches carry no captions of
       * their own, so the selected name is the only place the value is
       * spelled out. Other options keep a plain label.
       */
      const label = document.createElement('p');
      label.className = 'qv__option-label';
      const labelName = document.createElement('span');
      labelName.textContent = isColour ? name + ':' : name;
      label.appendChild(labelName);

      const labelValue = document.createElement('span');
      labelValue.className = 'qv__option-value';
      if (isColour) label.appendChild(labelValue);
      group.appendChild(label);

      const list = document.createElement('div');
      list.className = 'qv__swatches' + (isColour ? ' qv__swatches--image' : '');

      values.forEach((value) => {
        /**
         * An image swatch needs a variant image for this value. Roughly 10%
         * of the catalogue has colours with no image assigned yet, and those
         * fall back to a text button rather than a coloured dot — with 118
         * distinct colour names, many used once and some describing two
         * colours ("Camel w/ Beige Bow"), a name-to-colour map would be
         * wrong often enough to mislead.
         */
        const withImage = isColour
          ? product.variants.find((v) => v.options[index] === value && v.featured_image)
          : null;

        const btn = document.createElement('button');
        btn.type = 'button';
        btn.dataset.option = name;
        btn.dataset.optionIndex = String(index);
        btn.dataset.value = value;
        btn.setAttribute('aria-pressed', 'false');

        if (withImage) {
          btn.className = 'qv__swatch qv__swatch--image';
          const img = document.createElement('img');
          img.src = withImage.featured_image.src;
          img.alt = '';
          img.width = 112;
          img.height = 112;
          img.loading = 'lazy';
          btn.appendChild(img);
          // The name is not rendered, so it has to reach assistive tech and
          // hover some other way.
          btn.setAttribute('aria-label', value);
          btn.title = value;
        } else {
          btn.className = 'qv__swatch';
          btn.textContent = value;
        }

        // Show the pre-selected value as chosen, so the panel opens in the
        // state it actually holds.
        if (chosen[name] === value) {
          btn.classList.add('is-selected');
          btn.setAttribute('aria-pressed', 'true');
          if (isColour) labelValue.textContent = ' ' + value;
        }

        btn.addEventListener('click', () => {
          chosen[name] = value;
          if (isColour) labelValue.textContent = ' ' + value;
          list.querySelectorAll('.qv__swatch').forEach((b) => {
            const on = b.dataset.value === value;
            b.classList.toggle('is-selected', on);
            b.setAttribute('aria-pressed', on ? 'true' : 'false');
          });
          renderState();
        });

        list.appendChild(btn);
      });

      group.appendChild(list);
      el.options.appendChild(group);
    });
  };

  const load = async (handle) => {
    if (cache.has(handle)) return cache.get(handle);
    const res = await fetch(`/products/${handle}.js`);
    if (!res.ok) throw new Error(`product fetch failed: ${res.status}`);
    const data = await res.json();
    cache.set(handle, data);
    return data;
  };

  /**
   * Empty and disable the shared dialog before it becomes visible. Without
   * this reset, a slow request leaves the prior product's title, images, and
   * enabled add button interactive beneath the new trigger's modal.
   */
  const renderLoadingState = () => {
    product = null;
    chosen = {};
    editSubmitting = false;
    el.track.replaceChildren();
    el.title.textContent = '';
    el.price.textContent = '';
    el.footPrice.textContent = '';
    el.options.replaceChildren();
    el.description.textContent = '';
    if (el.details) {
      el.details.hidden = true;
      el.details.open = false;
    }
    el.submit.disabled = true;
    el.submit.textContent = strings.selectSize;
    el.status.textContent = 'Loading product details.';
    dialog.setAttribute('aria-busy', 'true');
  };

  /**
   * `compact` trades the browsing chrome for a decision panel: thumbnail
   * beside the title, no description or Buy It Now, narrow panel. Same
   * variant matching and the same custom-measurement flow underneath —
   * favorites needs the choosing, not the discovering.
   */
  /**
   * Compact anchors to the control that opened it instead of centring: it
   * belongs to one row of a list, so it opens directly under that row's
   * button and spans that row's text column -- which keeps the saved
   * product photography beside it visible rather than covered.
   */
  let anchorEl = null;

  const positionCompact = () => {
    if (!dialog.classList.contains('qv--compact') || !anchorEl?.isConnected) return;
    const trigger = anchorEl.getBoundingClientRect();

    // Never wider than the panel the trigger lives in.
    const host = anchorEl.closest('dialog, [data-wishlist-drawer]');
    const bounds = host ? host.getBoundingClientRect()
      : { left: 0, right: window.innerWidth };

    const GAP = 8;
    const left = Math.max(bounds.left + GAP, trigger.left - GAP);
    const right = Math.min(bounds.right - GAP, trigger.right + GAP);

    dialog.style.inlineSize = `${Math.max(240, right - left)}px`;
    dialog.style.insetInlineStart = `${left}px`;
    dialog.style.insetInlineEnd = 'auto';
    dialog.style.marginInline = '0';

    // Below the button, flipping above only when there is no room under it.
    const height = dialog.offsetHeight;
    let top = trigger.bottom + GAP;
    if (top + height > window.innerHeight - GAP) {
      const above = trigger.top - height - GAP;
      top = above >= GAP ? above : Math.max(GAP, window.innerHeight - height - GAP);
    }
    dialog.style.insetBlockStart = `${top}px`;
    dialog.style.insetBlockEnd = 'auto';
    dialog.style.marginBlock = '0';
  };

  const clearCompactPosition = () => {
    ['inlineSize', 'insetInlineStart', 'insetInlineEnd', 'insetBlockStart',
     'insetBlockEnd', 'marginInline', 'marginBlock'].forEach((prop) => {
      dialog.style[prop] = '';
    });
  };

  // The list scrolls under the panel, so the anchor moves.
  window.addEventListener('resize', positionCompact);
  document.addEventListener('scroll', positionCompact, true);

  /**
   * Re-place whenever the panel's own box settles. Measuring once at the end
   * of open() is too early: the gallery image has not loaded and the option
   * pills have not wrapped, so the panel is still at its max height and the
   * "no room below" branch wins when there was room all along.
   */
  if (typeof ResizeObserver === 'function') {
    new ResizeObserver(() => positionCompact()).observe(dialog);
  }

  const open = async (handle, href, { compact = false, anchor = null } = {}) => {
    const requestId = ++openRequestId;
    anchorEl = compact ? anchor : null;
    if (!compact) clearCompactPosition();
    dialog.classList.toggle('qv--compact', compact);
    renderLoadingState();
    if (!dialog.open) dialog.showModal();

    try {
      const loadedProduct = await load(handle);
      if (requestId !== openRequestId || !dialog.open) return;
      product = loadedProduct;
    } catch (err) {
      if (requestId !== openRequestId || !dialog.open) return;
      if (editContext) {
        el.status.textContent = 'Cart options could not be loaded. Please close this panel and try again.';
        dialog.setAttribute('aria-busy', 'false');
        return;
      }
      // Fetch failed — fall back to the destination the link already had.
      dialog.close();
      window.location.href = href;
      return;
    }

    el.title.textContent = product.title;
    renderPrice(el.price, product.price, product.compare_at_price);
    renderPrice(el.footPrice, product.price, product.compare_at_price);
    renderGallery(product);
    el.description.innerHTML = product.description || '';
    // No description, no disclosure — an empty SEE DETAILS is a dead control.
    if (el.details) {
      el.details.hidden = !product.description;
      el.details.open = false;
    }

    // Edit mode opens on the line's current variant, so its size and colour
    // read as already chosen.
    if (editContext) {
      const current = product.variants.find((v) => Number(v.id) === editContext.variantId);
      if (current) {
        const names = product.options.map((o) => (typeof o === 'string' ? o : o.name));
        chosen = {};
        names.forEach((n, i) => { chosen[n] = current.options[i]; });
      }
    }

    renderOptions();
    const editedVariant = editContext ? currentVariant() : null;
    resetMeasurement(editContext?.properties, Boolean(editContext && isCustomVariant(editedVariant)));
    renderState();
    dialog.setAttribute('aria-busy', 'false');
    // After the options render: the pill rows decide the panel's height, and
    // the height decides whether it opens below the button or above it.
    positionCompact();
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-quick-view]');
    if (!trigger) return;
    event.preventDefault();
    open(trigger.dataset.quickView, trigger.getAttribute('href'), {
      compact: trigger.hasAttribute('data-quick-view-compact'),
      anchor: trigger,
    });
  });

  dialog.querySelector('[data-qv-close]').addEventListener('click', () => dialog.close());

  // Click on the backdrop (the dialog itself, outside the panel) closes it.
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) dialog.close();
  });

  // Closing invalidates an in-flight fetch so it cannot repopulate a hidden
  // dialog or overwrite the next product after the shopper opens it again.
  dialog.addEventListener('close', () => {
    openRequestId += 1;
    dialog.classList.remove('qv--compact');
    anchorEl = null;
    clearCompactPosition();
    editContext = null;
    editSubmitting = false;
    dialog.setAttribute('aria-busy', 'false');
  });

  /**
   * A variant plus its line-item properties identifies a cart line: the same
   * hat at a different head measurement is a separate line, so the properties
   * have to be part of the match.
   */
  const lineSignature = (props) => JSON.stringify(
    Object.entries(props || {})
      .filter(([key]) => !key.startsWith('_'))
      .sort(([a], [b]) => a.localeCompare(b))
  );

  /**
   * Current quantity and line for this variant/properties pair, or null when
   * the cart could not be read at all.
   */
  const cartLineFor = async (variantId, properties) => {
    try {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!res.ok) return null;
      const cart = await res.json();
      const want = lineSignature(properties);
      const matches = cart.items.filter(
        (line) => line.variant_id === variantId && lineSignature(line.properties) === want
      );
      return {
        quantity: matches.reduce((total, line) => total + line.quantity, 0),
        item: matches[0] || null,
      };
    } catch (_error) {
      return null;
    }
  };

  /**
   * Adds the chosen variant. `checkout: true` sends the shopper straight to
   * checkout instead of announcing the add — the cart event is skipped in
   * that case, since opening the drawer over a page that is navigating away
   * would only flash.
   */
  const addChosenVariant = async ({ checkout = false } = {}) => {
    const v = currentVariant();
    if (!v) return;

    // A custom variant carries its head measurement as line-item properties,
    // collected inline rather than on the product page.
    let properties = null;
    if (isCustomVariant(v)) {
      const value = measurementValue();
      if (value === null) {
        openMeasurement();
        el.status.textContent = measurementRangeMessage();
        return;
      }
      properties = {
        'Head measurement': String(value),
        'Measurement unit': measurementUnit,
      };
    }

    el.submit.disabled = true;
    if (el.buyNow) el.buyNow.disabled = true;
    el.status.textContent = '';

    // Read before the POST so a lost response can be reconciled against a
    // known quantity rather than guessed at.
    const before = await cartLineFor(v.id, properties);

    const finishAdd = (item) => {
      if (checkout) {
        window.location.assign(window.Shopify?.routes?.root
          ? `${window.Shopify.routes.root}checkout`
          : '/checkout');
        return;
      }

      // Close the quick view before announcing, so the confirmation is not
      // stacked behind the modal backdrop it would otherwise appear under.
      dialog.close();

      document.dispatchEvent(
        new CustomEvent('cart:updated', { detail: { item } })
      );
    };

    let failure = null;

    try {
      const line = { id: v.id, quantity: 1 };
      if (properties) line.properties = properties;
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [line] }),
      });
      if (!res.ok) {
        // The server answered, so nothing was added and a retry is safe.
        const body = await res.text();
        let message = 'Could not add to cart';
        try {
          const parsed = JSON.parse(body);
          message = parsed.description || parsed.message || message;
        } catch (_parseError) {
          // Not JSON — keep the generic message rather than show raw markup.
        }
        throw Object.assign(new Error(message), { answered: true });
      }

      const added = await res.json();
      finishAdd((added.items && added.items[0]) || added);
    } catch (err) {
      /**
       * A rejected fetch means the response was lost, not necessarily the
       * request — the line may already be in the cart. Announcing a failure
       * that did not happen is what makes a shopper click again and end up
       * with two, so re-read the cart before believing it.
       */
      let landed = false;
      if (!err.answered && before) {
        const after = await cartLineFor(v.id, properties);
        if (after && after.quantity > before.quantity) {
          finishAdd(after.item);
          landed = true;
        }
      }
      if (!landed) failure = err.message || 'Could not add to cart';
    }

    renderState();
    // renderState() clears the status line, so the message has to be written
    // after it — inside a finally every add error was wiped the same tick it
    // was set, leaving a silent failure that invites a second click.
    if (failure) el.status.textContent = failure;
  };

  // Reveal the measurement panel and put the cursor in the field. renderState
  // relabels the button to "Add to cart" and keeps it disabled until a valid
  // number is entered.
  const openMeasurement = () => {
    measurementMode = true;
    renderState();
    positionCompact();
    window.requestAnimationFrame(() => {
      el.measurementInput?.focus();
      el.measurement?.scrollIntoView({ block: 'nearest' });
    });
  };

  const editedPropertiesFor = (variant) => {
    const properties = { ...(editContext?.properties || {}) };
    if (!isCustomVariant(variant)) {
      delete properties['Head measurement'];
      delete properties['Measurement unit'];
      return properties;
    }

    const value = measurementValue();
    if (value === null) throw new Error(measurementRangeMessage());
    properties['Head measurement'] = String(value);
    properties['Measurement unit'] = measurementUnit;
    return properties;
  };

  const submitCurrentChoice = async () => {
    if (editSubmitting) return;
    const v = currentVariant();
    if (!v) return;
    // For a custom size, the first click opens the panel rather than adding.
    if (isCustomVariant(v) && !measurementMode) {
      openMeasurement();
      return;
    }

    if (editContext) {
      const apply = editContext.onApply;
      if (typeof apply !== 'function') return;

      let properties;
      try {
        properties = editedPropertiesFor(v);
      } catch (error) {
        el.status.textContent = error.message;
        return;
      }

      editSubmitting = true;
      renderState();
      try {
        const updated = await apply(v, properties);
        if (updated === false) throw new Error('That cart item could not be updated.');
        dialog.close();
      } catch (error) {
        editSubmitting = false;
        renderState();
        el.status.textContent = error.message || 'That cart item could not be updated.';
      }
      return;
    }
    await addChosenVariant();
  };

  el.submit.addEventListener('click', submitCurrentChoice);
  el.buyNow?.addEventListener('click', () => addChosenVariant({ checkout: true }));

  el.measurementInput?.addEventListener('input', renderState);
  el.measurementInput?.addEventListener('keydown', (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      submitCurrentChoice();
    }
  });

  el.units.forEach((btn) => {
    btn.addEventListener('click', () => {
      measurementUnit = btn.dataset.qvUnit;
      el.units.forEach((b) => {
        const on = b === btn;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-pressed', String(on));
      });
      /**
       * 58 is an ordinary centimetre reading and an impossible inch one, so a
       * unit switch has to re-scale the bounds and re-check what is already
       * typed — renderState re-runs the submit gate against the new range.
       */
      applyMeasurementBounds();
      renderState();
      el.measurementInput?.focus();
    });
  });

  /**
   * Opens the popup to edit an existing cart line. The cart reuses this same
   * modal — pills, swatches, responsive sheet — instead of a separate editor.
   * onApply receives both the chosen variant and a fresh property object.
   */
  const openEdit = ({ handle, href, variantId, properties, onApply }) => {
    editContext = {
      variantId: Number(variantId),
      properties: { ...(properties || {}) },
      onApply,
    };
    open(handle, href);
  };

  window.MSPQuickView = { open, openEdit };
})();
